/**
 * Provider credentialing desk, the medical director's desk, cross-clinic.
 *
 * Both platform roles can READ this page, but only MEDICAL_DIRECTOR can decide:
 * the client's Q3 answer put credentialing on Dr. Joe's desk and billing on the
 * owner's. The owner still needs to see who is able to order labs, so the table
 * is shared and the decision controls are not. The API enforces the same split,
 * so hiding the buttons is a courtesy, not the security boundary.
 *
 * Two decisions live here, and keeping them visibly separate is the point of
 * the layout:
 *
 *   1. MCOS review of the credentialing application (approve / reject).
 *   2. LabCorp ordering-provider enrolment (pending / active / rejected).
 *
 * Approving (1) does not grant (2). Only an ACTIVE LabCorp status lets a doctor
 * order labs, so the two columns never merge into one "approved" badge, an
 * owner glancing at this table has to be able to tell "we vetted them" apart
 * from "the lab will accept their orders".
 *
 * Nurses appear in the same list but have no ordering status of their own: the
 * column says so instead: their orders are placed by MCOS Clinical.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
  inputClass,
} from '../components/ui';
import { IconAlert, IconCheck } from '../components/icons';

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

type ProviderType = 'DOCTOR' | 'NURSE';
type ApplicationStatus = 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';
type LabStatus = 'PENDING' | 'ACTIVE' | 'REJECTED';
type NppesStatus =
  | 'UNCHECKED'
  | 'VERIFIED'
  | 'NAME_MISMATCH'
  | 'NOT_FOUND'
  | 'INACTIVE'
  | 'NOT_INDIVIDUAL'
  | 'UNAVAILABLE';

interface NppesSnapshot {
  firstName: string | null;
  lastName: string | null;
  credential: string | null;
  primaryTaxonomy: string | null;
  state: string | null;
  status: string | null;
  note: string;
}

interface Provider {
  id: string;
  tenantId: string;
  type: ProviderType;
  legalFirstName: string;
  legalMiddleName: string | null;
  legalLastName: string;
  credentials: string;
  specialty: string | null;
  npi: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
  licenseExpiresAt: string | null;
  deaNumber: string | null;
  contactEmail: string;
  contactPhone: string;
  practiceName: string | null;
  practiceCity: string | null;
  practiceState: string | null;
  practiceFax: string | null;
  applicationStatus: ApplicationStatus;
  applicationSubmittedAt: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  labOrderingStatus: LabStatus;
  labOrderingStatusAt: string | null;
  labOrderingStatusReason: string | null;
  labcorpAccountNumber: string | null;
  labcorpProviderId: string | null;
  nppesStatus: NppesStatus;
  nppesCheckedAt: string | null;
  nppesSnapshotJson: NppesSnapshot | null;
  createdAt: string;
  tenant: { id: string; name: string; slug: string };
  user: { id: string; email: string; name: string; role: string; active: boolean };
}

interface ProvidersResponse {
  providers: Provider[];
  counts: Partial<Record<ApplicationStatus, number>>;
}

/* ------------------------------------------------------------------ *
 * Status presentation
 * ------------------------------------------------------------------ */

const APP_STYLE: Record<ApplicationStatus, string> = {
  DRAFT: 'bg-ink/5 text-ink-muted border border-ink/10',
  SUBMITTED: 'bg-amber-50 text-amber-700 border border-amber-200',
  UNDER_REVIEW: 'bg-sky-50 text-sky-700 border border-sky-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border border-red-200',
};

const LAB_STYLE: Record<LabStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border border-amber-200',
  ACTIVE: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border border-red-200',
};

/**
 * NPPES verdicts are evidence, not decisions, so only VERIFIED reads as good
 * and only the two "this NPI is wrong" verdicts read as bad. A mismatch or an
 * outage is amber: the reviewer's next move is to look, not to reject.
 */
const NPPES_STYLE: Record<NppesStatus, string> = {
  VERIFIED: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  NAME_MISMATCH: 'bg-amber-50 text-amber-700 border border-amber-200',
  UNAVAILABLE: 'bg-amber-50 text-amber-700 border border-amber-200',
  UNCHECKED: 'bg-ink/5 text-ink-muted border border-ink/10',
  NOT_FOUND: 'bg-red-50 text-red-700 border border-red-200',
  INACTIVE: 'bg-red-50 text-red-700 border border-red-200',
  NOT_INDIVIDUAL: 'bg-red-50 text-red-700 border border-red-200',
};

const NPPES_LABEL: Record<NppesStatus, string> = {
  VERIFIED: 'NPPES verified',
  NAME_MISMATCH: 'name mismatch',
  UNAVAILABLE: 'registry unreachable',
  UNCHECKED: 'not checked',
  NOT_FOUND: 'not in registry',
  INACTIVE: 'NPI deactivated',
  NOT_INDIVIDUAL: 'organisation NPI',
};

const APP_LABEL: Record<ApplicationStatus, string> = {
  DRAFT: 'draft',
  SUBMITTED: 'awaiting review',
  UNDER_REVIEW: 'under review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

function fullName(p: Provider): string {
  const middle = p.legalMiddleName ? ` ${p.legalMiddleName}` : '';
  return `${p.legalFirstName}${middle} ${p.legalLastName}, ${p.credentials}`;
}

function date(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

const FILTERS: { key: string; label: string; value?: ApplicationStatus }[] = [
  { key: 'all', label: 'All' },
  { key: 'SUBMITTED', label: 'Awaiting review', value: 'SUBMITTED' },
  { key: 'UNDER_REVIEW', label: 'Under review', value: 'UNDER_REVIEW' },
  { key: 'APPROVED', label: 'Approved', value: 'APPROVED' },
  { key: 'REJECTED', label: 'Rejected', value: 'REJECTED' },
];

export function PlatformProvidersPage() {
  const { user } = useAuth();
  const canDecide = user?.role === 'MEDICAL_DIRECTOR';
  const [filter, setFilter] = useState('SUBMITTED');
  const [typeFilter, setTypeFilter] = useState<'' | ProviderType>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const active = FILTERS.find((f) => f.key === filter);

  const query = useQuery({
    queryKey: ['platform', 'providers', filter, typeFilter],
    queryFn: () =>
      api.get<ProvidersResponse>(
        `/platform/providers${qs({
          applicationStatus: active?.value,
          type: typeFilter || undefined,
        })}`,
      ),
  });

  const counts = query.data?.counts ?? {};
  const awaiting = (counts.SUBMITTED ?? 0) + (counts.UNDER_REVIEW ?? 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Providers"
        subtitle="Doctor and nurse credentialing, NPI verification, and LabCorp ordering status, across every practice."
      />

      {!canDecide && (
        <div className="flex items-start gap-2 glass-standard rounded-sm2 px-4 py-3 text-sm text-ink-muted">
          <span className="mt-0.5 shrink-0 text-ink-muted">
            <IconAlert size={16} />
          </span>
          <p>
            <strong className="text-ink">View only.</strong> Credentialing decisions are the
            medical director's. You can see who is able to order labs, but approving providers and
            approving providers happens on Dr. Joe's account.
          </p>
        </div>
      )}

      {awaiting > 0 && canDecide && (
        <div className="flex items-start gap-2 rounded-sm2 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="mt-0.5 shrink-0">
            <IconAlert size={16} />
          </span>
          <p>
            <strong>
              {awaiting} application{awaiting === 1 ? '' : 's'} waiting on you.
            </strong>{' '}
            A provider cannot order labs until their application is approved and their LabCorp
            enrolment is set to active.
          </p>
        </div>
      )}

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-ink/5 px-5 py-3">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`rounded-sm2 px-2.5 py-1.5 text-xs font-medium transition ${
                filter === f.key
                  ? 'bg-[var(--brand-accent)] text-white'
                  : 'text-ink-muted hover:bg-ink/5'
              }`}
            >
              {f.label}
              {f.value && counts[f.value] !== undefined && (
                <span className="ml-1.5 opacity-70">{counts[f.value]}</span>
              )}
            </button>
          ))}
          <div className="ml-auto">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as '' | ProviderType)}
              className={`${inputClass} w-40 py-1.5 text-xs`}
            >
              <option value="">Doctors &amp; nurses</option>
              <option value="DOCTOR">Doctors only</option>
              <option value="NURSE">Nurses only</option>
            </select>
          </div>
        </div>

        {query.isLoading ? (
          <div className="p-5">
            <LoadingRows rows={5} />
          </div>
        ) : query.isError ? (
          <div className="p-5">
            <ErrorState error={query.error} retry={() => void query.refetch()} />
          </div>
        ) : (query.data?.providers.length ?? 0) === 0 ? (
          <div className="p-5">
            <EmptyState
              title="Nothing here"
              body="No provider applications match this filter."
            />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Provider</Th>
                <Th>Clinic</Th>
                <Th>Licensure</Th>
                <Th>Application</Th>
                <Th>Lab ordering</Th>
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {query.data?.providers.map((p) => (
                <ProviderRow
                  key={p.id}
                  provider={p}
                  canDecide={canDecide}
                  open={openId === p.id}
                  onToggle={() => setOpenId(openId === p.id ? null : p.id)}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Row + review drawer
 * ------------------------------------------------------------------ */

function ProviderRow({
  provider,
  canDecide,
  open,
  onToggle,
}: {
  provider: Provider;
  canDecide: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={open ? 'bg-ink/5' : undefined}>
        <Td>
          <div className="font-medium text-ink">{fullName(provider)}</div>
          <div className="text-xs text-ink-muted">
            {provider.type === 'DOCTOR' ? 'Doctor' : 'Nurse'}
            {provider.specialty ? ` · ${provider.specialty}` : ''}
          </div>
        </Td>
        <Td>
          <div className="text-ink">{provider.tenant.name}</div>
          <div className="text-xs text-ink-muted">{provider.tenant.slug}</div>
        </Td>
        <Td>
          <div className="text-xs text-ink-muted">
            {provider.npi ? (
              <span>
                NPI <span className="font-mono">{provider.npi}</span>
              </span>
            ) : (
              <span className="text-ink-muted">no NPI</span>
            )}
          </div>
          {provider.npi && provider.nppesStatus !== 'UNCHECKED' && (
            <div className="mt-1">
              <Pill className={NPPES_STYLE[provider.nppesStatus]}>
                {NPPES_LABEL[provider.nppesStatus]}
              </Pill>
            </div>
          )}
          <div className="text-xs text-ink-muted">
            {provider.licenseNumber
              ? `${provider.licenseState ?? '??'} ${provider.licenseNumber}`
              : '-'}
          </div>
        </Td>
        <Td>
          <Pill className={APP_STYLE[provider.applicationStatus]}>
            {APP_LABEL[provider.applicationStatus]}
          </Pill>
          <div className="mt-1 text-[11px] text-ink-muted">
            {provider.applicationStatus === 'DRAFT'
              ? 'not submitted'
              : `submitted ${date(provider.applicationSubmittedAt)}`}
          </div>
        </Td>
        <Td>
          {provider.type === 'DOCTOR' ? (
            <>
              <Pill className={LAB_STYLE[provider.labOrderingStatus]}>
                {provider.labOrderingStatus.toLowerCase()}
              </Pill>
              {provider.labOrderingStatus === 'ACTIVE' && (
                <div className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700">
                  <IconCheck size={12} /> can order labs
                </div>
              )}
            </>
          ) : (
            /*
             * A nurse has no lab ordering column to show. Dr Joe's answer to
             * Q5 was that a nurse is never the ordering provider and never
             * orders under his NPI, so there is no status here that could
             * become "yes" later.
             */
            <span className="text-xs text-ink-muted">cannot order labs</span>
          )}
        </Td>
        <Td align="right">
          <Button size="sm" variant="secondary" onClick={onToggle}>
            {open ? 'Close' : canDecide ? 'Review' : 'Details'}
          </Button>
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="bg-ink/5 px-5 py-4">
            <ReviewPanel provider={provider} canDecide={canDecide} />
          </td>
        </tr>
      )}
    </>
  );
}

function ReviewPanel({
  provider,
  canDecide,
}: {
  provider: Provider;
  canDecide: boolean;
}) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['platform', 'providers'] });
  };

  const [notes, setNotes] = useState(provider.reviewNotes ?? '');
  const [labReason, setLabReason] = useState('');
  const [labAccount, setLabAccount] = useState(provider.labcorpAccountNumber ?? '');

  const review = useMutation({
    mutationFn: (decision: 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED') =>
      api.post(`/platform/providers/${provider.id}/review`, {
        decision,
        notes: notes.trim() || undefined,
      }),
    onSuccess: invalidate,
  });

  const labStatus = useMutation({
    mutationFn: (status: LabStatus) =>
      api.post(`/platform/providers/${provider.id}/lab-status`, {
        status,
        reason: labReason.trim() || undefined,
        labcorpAccountNumber: labAccount.trim() || undefined,
      }),
    onSuccess: invalidate,
  });

  const recheck = useMutation({
    mutationFn: () => api.post(`/platform/providers/${provider.id}/verify-npi`, {}),
    onSuccess: invalidate,
  });

  const error = review.error ?? labStatus.error ?? recheck.error;
  const nppes = provider.nppesSnapshotJson;

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {/* --- what they submitted --- */}
      <div className="space-y-3 text-sm">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Submitted details
        </h3>
        <Detail label="Legal name" value={fullName(provider)} />
        <Detail label="Account" value={`${provider.user.name} · ${provider.user.email}`} />
        <Detail label="Contact" value={`${provider.contactEmail} · ${provider.contactPhone}`} />
        <Detail label="Individual NPI" value={provider.npi} mono />

        {provider.npi && (
          <div className="glass-standard rounded-sm2 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <Pill className={NPPES_STYLE[provider.nppesStatus]}>
                {NPPES_LABEL[provider.nppesStatus]}
              </Pill>
              {canDecide && (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={recheck.isPending}
                  onClick={() => recheck.mutate()}
                >
                  Re-check
                </Button>
              )}
            </div>
            {nppes && (
              <div className="mt-2 space-y-1 text-[11px] text-ink-muted">
                <div>
                  Registry name:{' '}
                  <strong className="text-ink">
                    {[nppes.firstName, nppes.lastName].filter(Boolean).join(' ') || '-'}
                  </strong>
                  {nppes.credential ? `, ${nppes.credential}` : ''}
                </div>
                {nppes.primaryTaxonomy && <div>{nppes.primaryTaxonomy}</div>}
                {nppes.state && <div>Registered in {nppes.state}</div>}
                <div className="text-ink-muted">{nppes.note}</div>
              </div>
            )}
            {provider.nppesCheckedAt && (
              <div className="mt-1.5 text-[11px] text-ink-muted">
                Checked {date(provider.nppesCheckedAt)}
              </div>
            )}
          </div>
        )}
        <Detail
          label="State license"
          value={
            provider.licenseNumber
              ? `${provider.licenseNumber} (${provider.licenseState ?? '-'})${
                  provider.licenseExpiresAt ? ` · expires ${date(provider.licenseExpiresAt)}` : ''
                }`
              : null
          }
        />
        <Detail label="DEA" value={provider.deaNumber} mono />
        <Detail
          label="Practice"
          value={
            provider.practiceName
              ? `${provider.practiceName}${
                  provider.practiceCity
                    ? `, ${provider.practiceCity}, ${provider.practiceState ?? ''}`
                    : ''
                }`
              : null
          }
        />
        <Detail label="Fax" value={provider.practiceFax} />
      </div>

      {/* --- decision 1: MCOS review --- */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          1 · Credentialing review
        </h3>
        {!canDecide ? (
          <ReadOnlyDecision
            status={APP_LABEL[provider.applicationStatus]}
            notes={provider.reviewNotes}
            at={provider.reviewedAt}
          />
        ) : (
        <>
        <Field label="Review notes" hint="Required when rejecting. Recorded in the audit log.">
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputClass}
            placeholder="Verified NPI against NPPES; license current."
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            loading={review.isPending}
            disabled={provider.applicationStatus === 'APPROVED'}
            onClick={() => review.mutate('APPROVED')}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={review.isPending}
            onClick={() => review.mutate('UNDER_REVIEW')}
          >
            Mark under review
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={review.isPending}
            disabled={notes.trim().length === 0}
            title={notes.trim().length === 0 ? 'A rejection must record a reason' : undefined}
            onClick={() => review.mutate('REJECTED')}
          >
            Reject
          </Button>
        </div>
        {provider.reviewedAt && (
          <p className="text-[11px] text-ink-muted">Last reviewed {date(provider.reviewedAt)}.</p>
        )}
        </>
        )}
      </div>

      {/* --- decision 2: LabCorp enrolment --- */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          2 · LabCorp ordering enrolment
        </h3>

        {provider.type === 'NURSE' ? (
          <p className="glass-standard rounded-sm2 px-3 py-2.5 text-xs text-ink-muted">
            Nurses do not hold ordering privileges. Their lab requests are held in the
            MCOS Clinical team, using an ordering clinician licensed in the patient's state.
          </p>
        ) : !canDecide ? (
          <ReadOnlyDecision
            status={provider.labOrderingStatus.toLowerCase()}
            notes={provider.labOrderingStatusReason}
            at={provider.labOrderingStatusAt}
          />
        ) : (
          <>
            <p className="text-xs text-ink-muted">
              Only <strong>active</strong> unlocks lab ordering. Set this to match what LabCorp has
              actually approved, not what we hope they will.
            </p>
            <Field label="LabCorp account number" hint="Optional, once LabCorp issues one.">
              <input
                value={labAccount}
                onChange={(e) => setLabAccount(e.target.value)}
                className={inputClass}
                placeholder="e.g. 01234567"
              />
            </Field>
            <Field label="Reason / reference">
              <input
                value={labReason}
                onChange={(e) => setLabReason(e.target.value)}
                className={inputClass}
                placeholder="Enrolment confirmed by LabCorp rep, 2026-09-07"
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                loading={labStatus.isPending}
                disabled={
                  provider.applicationStatus !== 'APPROVED' ||
                  provider.labOrderingStatus === 'ACTIVE'
                }
                title={
                  provider.applicationStatus !== 'APPROVED'
                    ? 'Approve the credentialing application first'
                    : undefined
                }
                onClick={() => labStatus.mutate('ACTIVE')}
              >
                Set active
              </Button>
              <Button
                size="sm"
                variant="secondary"
                loading={labStatus.isPending}
                onClick={() => labStatus.mutate('PENDING')}
              >
                Set pending
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={labStatus.isPending}
                onClick={() => labStatus.mutate('REJECTED')}
              >
                Set rejected
              </Button>
            </div>
            {provider.labOrderingStatusReason && (
              <p className="text-[11px] text-ink-muted">
                {provider.labOrderingStatusReason} · {date(provider.labOrderingStatusAt)}
              </p>
            )}
          </>
        )}

        {error && (
          <p className="rounded-sm2 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error instanceof Error ? error.message : 'Something went wrong.'}
          </p>
        )}
      </div>
    </div>
  );
}

/** What the billing owner sees in place of the decision controls. */
function ReadOnlyDecision({
  status,
  notes,
  at,
}: {
  status: string;
  notes: string | null;
  at: string | null;
}) {
  return (
    <div className="glass-standard rounded-sm2 px-3 py-2.5 text-xs text-ink-muted">
      <div>
        Currently <strong className="text-ink">{status}</strong>
        {at ? ` · ${date(at)}` : ''}
      </div>
      {notes && <div className="mt-1 text-ink-muted">{notes}</div>}
    </div>
  );
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={`text-ink ${mono ? 'font-mono text-xs' : 'text-sm'}`}>
        {value ?? <span className="text-ink-muted">-</span>}
      </div>
    </div>
  );
}
